/**
 * Billing Service
 *
 * Fetches subscription overview and invoice list from /api/billing.
 * PDF download is handled inline in the component via the apiClient directly
 * (blob response type) to enable per-row loading states.
 */

import apiClient from './api';

// ---------------------------------------------------------------------------
// Response types — mirrors billing.service.ts on the backend exactly
// ---------------------------------------------------------------------------

export interface BillingSubscriptionItem {
  name: string;
  productId: number;
  variationId: number;
  total: number;
}

export interface BillingSubscription {
  id: number;
  kind: 'plan' | 'addon';
  status: string;
  amount: number;
  currency: string;
  billingPeriod: string;
  billingInterval: number;
  nextRenewal: string | null;
  startDate: string | null;
  viewUrl: string | null;
  items: BillingSubscriptionItem[];
}

export type BillingOverview =
  | { hasBilling: false; reason?: 'agency_managed' | 'no_account' }
  | {
      hasBilling: true;
      subscription: BillingSubscription | null;
      addonSubscriptions: BillingSubscription[];
      customer: { email: string };
    };

export interface BillingInvoice {
  orderId: number;
  /** WooCommerce order number (not the invoice number). */
  number: string;
  /** PDF invoice number; null when no invoice document exists yet. */
  invoiceNumber: string | null;
  date: string | null;
  amount: number;
  currency: string;
  status: string;
  hasInvoice: boolean;
}

// ---------------------------------------------------------------------------
// Scope helpers
// ---------------------------------------------------------------------------

export type BillingScope = 'team' | 'agency';

function basePath(scope: BillingScope): string {
  return scope === 'agency' ? '/api/agency/billing' : '/api/billing';
}

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

export const billingService = {
  async getOverview(scope: BillingScope = 'team'): Promise<BillingOverview> {
    const response = await apiClient.get<{ success: boolean; data: BillingOverview }>(
      `${basePath(scope)}/overview`
    );
    return response.data.data;
  },

  async getInvoices(scope: BillingScope = 'team'): Promise<BillingInvoice[]> {
    const response = await apiClient.get<{ success: boolean; data: BillingInvoice[] }>(
      `${basePath(scope)}/invoices`
    );
    return response.data.data;
  },

  /**
   * Download a PDF invoice as a browser download.
   * Uses blob responseType so the JWT auth header is included automatically.
   */
  async downloadInvoicePdf(
    orderId: number,
    invoiceNumber: string,
    scope: BillingScope = 'team',
  ): Promise<void> {
    const response = await apiClient.get(`${basePath(scope)}/invoices/${orderId}/pdf`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data as Blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `invoice-${invoiceNumber}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  },
};
